import { type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";
import { Skeleton } from "./Skeleton";

/* Phase 4.8 — one loading language.
 *
 * Three were in use, interchangeably and without a rule: Skeleton in 103
 * places, the literal string "Loading…" in 23, and a spinning or pulsing
 * icon in 95. Which one a screen used came down to who wrote it, so the
 * same product said "this is loading" three different ways, sometimes twice
 * on one page.
 *
 * The rule this settles on:
 *   region  →  Skeleton, shaped like the content that is coming
 *   inline  →  Spinner (inside a button, beside a live value)
 *   never   →  the words "Loading…" as a region placeholder
 *
 * Why the words lose: a text placeholder collapses the layout to one line,
 * so the page visibly jumps when data lands — the exact reflow a skeleton
 * exists to prevent — and it tells the reader nothing about the shape of
 * what is coming.
 *
 * ── The part that is easy to get wrong ───────────────────────────────────
 * Skeleton is `aria-hidden`, deliberately: a screen reader must not read out
 * eight grey bars. But that means naively replacing "Loading…" with bare
 * skeletons REMOVES the only thing a non-sighted user had — the region goes
 * silent and simply appears to be empty until it is not.
 *
 * So this wraps the bars in a live region that announces the wait once, and
 * carries `aria-busy` so assistive tech knows the content is in flight.
 * Sighted users get the shape; everyone else gets told. Replacing the text
 * with skeletons alone would have been a downgrade dressed as consistency.
 */

export interface LoadingRegionProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  /** Number of placeholder lines. Match it to the content that is coming. */
  lines?: number;
  /** Announced to assistive tech. Say what is loading, not just "loading". */
  label?: string;
  /** Height of each bar. */
  lineHeight?: number | string;
  /** Uniform bars read as a table; varied ones read as prose. */
  varied?: boolean;
}

const VARIED_WIDTHS = ["92%", "78%", "85%", "64%", "88%", "71%"];

export function LoadingRegion({
  lines = 3,
  label = "Loading",
  lineHeight = 14,
  varied = true,
  className,
  ...rest
}: LoadingRegionProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      className={cn("rc-loading-region", className)}
      {...rest}
    >
      <span className="sr-only">{label}</span>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          height={lineHeight}
          width={varied ? VARIED_WIDTHS[i % VARIED_WIDTHS.length] : "100%"}
          radius={4}
        />
      ))}
    </div>
  );
}
